import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { FormSection, RequiredFieldsNote } from '@/components/ui/form-section';
import { useEmployeeFormOptions } from '@/hooks/employees';
import { useHireApplicant } from '@/hooks/applicants';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { parseMoneyInput } from '@shared/money';
import {
  suggestNameSplit,
  type ApplicantDetail,
  type HireApplicantResult,
} from '@shared/schemas/applicant';

/*
 * Hiring an applicant — ROADMAP.md §9.10.
 *
 * **The name split is the whole reason this is a dialog and not a button.** The
 * careers form has one Name field, so `job_application.full_name` holds
 * "Maria de la Cruz" as a single string, and `employee` needs a first and a last
 * name. Splitting that automatically is guesswork on any name that is not
 * exactly two words, and it is not undoable once stored — data-model.md §7 makes
 * the same call for `lead` and says a human separates it if it ever matters.
 *
 * So the suggestion below is *a suggestion*, shown in an editable field with the
 * original beside it. Somebody looks at it before it becomes the name on a
 * payslip.
 */

export function HireDialog({
  detail,
  open,
  onOpenChange,
}: {
  detail: ApplicantDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <HireForm
            detail={detail}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function HireForm({ detail, onDone }: { detail: ApplicantDetail; onDone: () => void }) {
  const toast = useToast();
  const options = useEmployeeFormOptions();
  const suggestion = suggestNameSplit(detail.fullName);

  const [firstName, setFirstName] = useState(suggestion.firstName);
  const [lastName, setLastName] = useState(suggestion.lastName);
  const [email, setEmail] = useState(detail.email);
  const [maxJobsPerDay, setMaxJobsPerDay] = useState('3');
  const [rate, setRate] = useState('');
  const [positionId, setPositionId] = useState(detail.positionId);
  const [hireDate, setHireDate] = useState(detail.availableStartDate ?? '');
  const [rateError, setRateError] = useState<string | undefined>(undefined);

  /** Set once, after the call that actually created the employee. */
  const [handover, setHandover] = useState<HireApplicantResult | null>(null);

  const hire = useHireApplicant(detail.id);
  const error = hire.error instanceof ApiError ? hire.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  if (handover) return <Handover result={handover} onDone={onDone} />;

  function submit(event: FormEvent) {
    event.preventDefault();

    const money = parseMoneyInput(rate);
    if (!money.ok) {
      setRateError(money.message);
      return;
    }
    setRateError(undefined);

    hire.mutate(
      {
        firstName,
        lastName,
        email: email.trim(),
        maxJobsPerDay: Number(maxJobsPerDay) || 3,
        hourlyRateCents: money.cents,
        positionId: positionId === '' ? null : positionId,
        hireDate: hireDate === '' ? null : hireDate,
      },
      {
        onSuccess: (result) => {
          if (!result.created) {
            /*
             * **The idempotent answer.** Hiring twice returns the employee the
             * first call made — there is no second password, because none was
             * generated. Saying so plainly beats a handover screen with an empty
             * box on it.
             */
            toast.success(`${result.employeeName} was already hired from this application.`);
            onDone();
            return;
          }
          setHandover(result);
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">Hire {detail.fullName}</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        This creates their employee record and their login, and marks the application hired.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-6">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <FormSection
          title="Their name"
          description="The careers form asks for one name. Check the split before it becomes the name on their record — we would rather you corrected it than guessed."
        >
          <p className="rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2 text-body text-ink-700">
            <span className="text-meta text-ink-500">They wrote: </span>
            {detail.fullName}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="First name"
              name="firstName"
              required
              value={firstName}
              error={fieldErrors.firstName}
              onChange={(event) => {
                setFirstName(event.target.value);
              }}
            />
            <Field
              label="Last name"
              name="lastName"
              required
              value={lastName}
              error={fieldErrors.lastName}
              onChange={(event) => {
                setLastName(event.target.value);
              }}
            />
          </div>
        </FormSection>

        <FormSection
          title="How they will sign in"
          description="Prefilled from their application. A cleaner with no email of their own gets a username instead — add that on their employee record afterwards."
        >
          <Field
            label="Email"
            name="email"
            type="email"
            required
            value={email}
            error={fieldErrors.email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </FormSection>

        <FormSection title="The work">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="hire-position">Position</Label>
              <select
                id="hire-position"
                name="positionId"
                value={positionId}
                onChange={(event) => {
                  setPositionId(event.target.value);
                }}
                className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
              >
                <option value="">Not set</option>
                {(options.data?.positions ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
              </select>
              <p className="text-meta text-ink-500">
                Defaults to the job they applied for
                {detail.positionName === null ? '' : ` — ${detail.positionName}`}.
              </p>
            </div>

            <Field
              label="Hire date"
              name="hireDate"
              type="date"
              value={hireDate}
              error={fieldErrors.hireDate}
              onChange={(event) => {
                setHireDate(event.target.value);
              }}
              hint={
                detail.availableStartDate
                  ? 'Prefilled with the date they said they can start.'
                  : undefined
              }
            />

            <Field
              label="Max jobs a day"
              name="maxJobsPerDay"
              type="number"
              min={1}
              max={20}
              required
              value={maxJobsPerDay}
              error={fieldErrors.maxJobsPerDay}
              onChange={(event) => {
                setMaxJobsPerDay(event.target.value);
              }}
              hint="Scheduling will not assign more than this in one day."
            />

            <Field
              label="Hourly rate"
              name="hourlyRate"
              inputMode="decimal"
              value={rate}
              error={rateError ?? fieldErrors.hourlyRateCents}
              onChange={(event) => {
                setRate(event.target.value);
                setRateError(undefined);
              }}
              placeholder="18.00"
              hint={
                detail.desiredPay === null
                  ? 'Admin only. Nobody sees anyone else’s rate.'
                  : `They asked for ${detail.desiredPay}.`
              }
            />
          </div>
        </FormSection>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={hire.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={hire.isPending}>
            {hire.isPending ? 'Hiring…' : 'Hire and create their login'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * The one-time password.
 *
 * A screen rather than a toast, because it cannot be retrieved: it was generated
 * on the server, hashed, and the readable form exists only in this response.
 * Same treatment as the employee dialog, for the same reason.
 */
function Handover({ result, onDone }: { result: HireApplicantResult; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const password = result.temporaryPassword ?? '';

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">{result.employeeName} is hired</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Their employee record and login are set up. Give them these details over something private —
        the password is shown once and is not stored anywhere it can be read back.
      </Dialog.Description>

      <dl className="mt-5 space-y-3 rounded-sm border border-ink-300 bg-ink-100/40 p-4">
        <div>
          <dt className="text-meta text-ink-500">Temporary password</dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2">
            <code className="min-w-0 break-all rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-body">
              {password}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(password).then(() => {
                  setCopied(true);
                });
              }}
            >
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-meta text-ink-500">
        Ask them to change it after their first sign-in. Their skills, address and anything else go
        on their employee record.
      </p>

      <div className="mt-6 flex justify-end">
        <Button onClick={onDone}>I have taken a copy</Button>
      </div>
    </>
  );
}
